import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Banknote, CalendarDays, CheckCheck, FileText, type LucideIcon } from 'lucide-react'
import { apiJson, type Page } from '../api'

interface NotificationItem {
  id: string
  type: string
  title: string
  body: string
  link: string
  read: boolean
  createdAt: string
}

const typeIcons: Record<string, { icon: LucideIcon; tint: string }> = {
  'leave.requested': { icon: CalendarDays, tint: 'bg-amber-50 text-amber-600' },
  'leave.decided': { icon: CalendarDays, tint: 'bg-emerald-50 text-emerald-600' },
  'document.uploaded': { icon: FileText, tint: 'bg-blue-50 text-blue-600' },
  'payslip.issued': { icon: Banknote, tint: 'bg-emerald-50 text-emerald-600' },
}

function iconFor(type: string) {
  return typeIcons[type] ?? { icon: Bell, tint: 'bg-slate-100 text-slate-500' }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

const POLL_MS = 30000

export default function NotificationsBell() {
  const navigate = useNavigate()
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const refreshCount = useCallback(async () => {
    try {
      const data = await apiJson<{ count: number }>('/notifications/unread-count')
      setUnread(data.count)
    } catch {
      // badge is best-effort
    }
  }, [])

  useEffect(() => {
    refreshCount()
    const t = setInterval(refreshCount, POLL_MS)
    return () => clearInterval(t)
  }, [refreshCount])

  // close on outside click
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const loadList = async () => {
    setLoadingList(true)
    try {
      const data = await apiJson<Page<NotificationItem>>('/notifications?size=10')
      setItems(data.data)
    } catch {
      // list is best-effort
    } finally {
      setLoadingList(false)
    }
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) loadList()
  }

  const openItem = async (n: NotificationItem) => {
    setOpen(false)
    if (!n.read) {
      apiJson(`/notifications/${n.id}/read`, { method: 'POST' })
        .then(refreshCount)
        .catch(() => {})
    }
    if (n.link) navigate(n.link)
  }

  const markAll = async () => {
    try {
      await apiJson('/notifications/read-all', { method: 'POST' })
      setItems((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnread(0)
    } catch {
      // best-effort
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={toggle}
        className={`relative rounded-xl p-2.5 transition-colors hover:bg-slate-100 hover:text-slate-700 ${open ? 'bg-slate-100 text-slate-700' : 'text-slate-500'}`}
        title="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white ring-2 ring-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl animate-fade-up sm:w-96">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-bold tracking-tight">Notifications</p>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700"
              >
                <CheckCheck size={13} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loadingList ? (
              <div className="flex h-24 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-center">
                <Bell size={20} className="text-slate-300" />
                <p className="mt-2 text-xs text-slate-400">No notifications yet.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => {
                  const { icon: Icon, tint } = iconFor(n.type)
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => openItem(n)}
                        className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? '' : 'bg-brand-50/40'}`}
                      >
                        <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
                          <Icon size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold">{n.title}</span>
                            {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
                          </span>
                          {n.body && <span className="mt-0.5 block truncate text-xs text-slate-500">{n.body}</span>}
                          <span className="mt-0.5 block text-[11px] text-slate-400">{relativeTime(n.createdAt)}</span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
